import React, {useState} from "react";
import {Link} from "react-router";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import StarRateIcon from "@material-ui/icons/StarRate";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import ConfirmDialog from "common_components/confirm_dialog";

import {Organisation, OrganisationId} from "common/types/organisation";
import {Topic} from "common/types/topic";
import {
  Workflow,
  WorkflowInput,
  WorkflowInputId,
  Prompt,
  WorkflowContext,
  ContextId,
  WorkflowInstanceId,
  WorkflowInstanceDetail,
} from "common/flowmaster/types/workflow";
import {
  WorkflowTaskId,
  WorkflowTask,
  ActionDefinition,
} from "common/flowmaster/types/task_config";
import {Checkbox, Chip, FormControlLabel, Tooltip} from "@material-ui/core";
import {Lock, LockOpen} from "@material-ui/icons";
import {WorkflowEditor} from "./workflow_editor";
import {WorkflowLockConfirmDialog} from "./dialogs/workflow_lock_confirm_dialog";
import {DataAssetLockConfirmDialog} from "./dialogs/data_asset_lock_confirm_dialog";
import {DataAssetDuplicateConfirmDialog} from "./dialogs/data_asset_duplicate_confirm_dialog";

interface WorkflowDetailProps {
  organisationId: OrganisationId;
  organisation: Organisation & {is_central_admin?: boolean};
  workflow: Workflow;
  updateWorkflow: (WorkflowInfo) => void;
  duplicateWorkflow: () => void;
  deleteWorkflow: () => void;

  addWorkflowInput: (name) => void;
  updateWorkflowContext: (workflowContextId: ContextId) => void;
  updateWorkflowInput: (
    workflowInputId: WorkflowInputId,
    data: WorkflowInput,
  ) => void;
  removeWorkflowInput: (workflowInputId: WorkflowInputId) => void;
  reorderWorkflowInputs: (
    workflowInputId: WorkflowInputId,
    order: number,
  ) => void;

  addWorkflowTask: (name) => void;
  updateWorkflowTask: (
    workflowTaskId: WorkflowTaskId,
    data: WorkflowTask,
  ) => void;
  removeWorkflowTask: (workflowInputId: WorkflowInputId) => void;
  reorderWorkflowTasks: (
    id: WorkflowTaskId,
    movedItemId: WorkflowTaskId,
  ) => void;
  addPrerequisite: () => void;
  updatePrerequisiteInputValue: (
    id: WorkflowInstanceId,
    inputId: WorkflowInputId,
    value: string,
  ) => void;
  removePrerequisite: (id: WorkflowInstanceId) => void;
  updateWorkflowInstanceName: (id: WorkflowInstanceId, name: string) => void;
  actionDefinitions: ActionDefinition[];
  workflows: Workflow[];
  prompts: Prompt[];
  contexts: WorkflowContext[];
  embeddingTopics: Topic[];
  existingPrerequisites: WorkflowInstanceDetail[];
}

const styles = {
  topBar: {
    icon: {
      height: 40,
      width: 40,
      color: "#3d3d3d",
    },
  },
};

const WorkflowDetail: React.FC<WorkflowDetailProps> = ({
  organisationId,
  organisation,
  workflow,
  updateWorkflow,
  duplicateWorkflow,
  deleteWorkflow,
  updateWorkflowContext,
  addWorkflowInput,
  updateWorkflowInput,
  removeWorkflowInput,
  reorderWorkflowInputs,
  addWorkflowTask,
  updateWorkflowTask,
  removeWorkflowTask,
  reorderWorkflowTasks,
  addPrerequisite,
  updatePrerequisiteInputValue,
  removePrerequisite,
  updateWorkflowInstanceName,
  actionDefinitions,
  workflows,
  existingPrerequisites,
  prompts,
  contexts,
  embeddingTopics,
}) => {
  const [name, setName] = useState<string>("");
  const [showingDeleteDialog, showDeleteDialog] = useState<boolean>(false);
  const [showingDuplicateDialog, showDuplicateDialog] = useState<boolean>(
    false,
  );
  const [showingToggleLockDialog, showLockWorkflowDialog] = useState<boolean>(
    false,
  );
  const [allowEditing, setAllowEditing] = useState<boolean>(
    Boolean(organisation.is_central_admin),
  );

  const setNameValue = event => setName(event.target.value);

  const save = async () => {
    if (name.length > 0) {
      await updateWorkflow({name});
      setName("");
    }
  };

  const onToggleUpdate = async (
    property: "is_star" | "is_archived" | "is_data_asset" | "is_locked",
  ) => {
    await updateWorkflow({[property]: !workflow[property]});
  };

  const onDelete = () => {
    deleteWorkflow();
  };

  const StarIcon = workflow.is_star ? StarRateIcon : StarBorderIcon;
  const ArchiveActionIcon = workflow.is_archived ? UnarchiveIcon : ArchiveIcon;

  const LockIcon = workflow.is_locked ? Lock : LockOpen;

  return (
    <div
      style={{
        padding: "1em",
        display: "flex",
        alignItems: "flex-start",
        width: "calc(100% - 2em)",
      }}
    >
      <div style={{width: "100%"}}>
        <div style={{display: "flex", justifyContent: "space-between"}}>
          <h1>Workflow editor</h1>
          <TopMenu
            organisationId={organisationId}
            showAllowEditingCheckbox={!organisation.is_central_admin}
            workflow={workflow}
            showDuplicateDialog={showDuplicateDialog}
            allowEditing={allowEditing}
            setAllowEditing={setAllowEditing}
          />
        </div>
        <div style={{display: "flex", flexDirection: "column", gap: "1em"}}>
          <div style={{display: "flex", justifyContent: "space-between"}}>
            <div
              style={{
                gap: "1em",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <TextField
                label="Name"
                value={name || workflow.name}
                onChange={setNameValue}
                onBlur={() => save()}
                style={{width: "30em"}}
              />
              <div
                style={{cursor: "pointer"}}
                onClick={() => onToggleUpdate("is_star")}
              >
                <StarIcon
                  style={{
                    ...styles.topBar.icon,
                    color: "#fbc02d",
                  }}
                />
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => onToggleUpdate("is_archived")}
              >
                <ArchiveActionIcon style={styles.topBar.icon} />
              </div>
              {(!workflow.is_locked || !workflow.is_data_asset) && (
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => showLockWorkflowDialog(true)}
                >
                  <LockIcon style={styles.topBar.icon} />
                </div>
              )}
              {workflow.is_archived && (
                <Chip label="Archived" color="primary" />
              )}
              {workflow.is_data_asset && (
                <Chip label="Data Asset" color="primary" />
              )}
              {workflow.is_locked && <Chip label="Locked" color="primary" />}
            </div>
            <Button variant="contained" onClick={() => showDeleteDialog(true)}>
              Delete
            </Button>
          </div>
          <WorkflowEditor
            organisationId={organisationId}
            workflow={workflow}
            updateWorkflowContext={updateWorkflowContext}
            addWorkflowInput={addWorkflowInput}
            updateWorkflowInput={updateWorkflowInput}
            removeWorkflowInput={removeWorkflowInput}
            reorderWorkflowInputs={reorderWorkflowInputs}
            addWorkflowTask={addWorkflowTask}
            updateWorkflowTask={updateWorkflowTask}
            removeWorkflowTask={removeWorkflowTask}
            reorderWorkflowTasks={reorderWorkflowTasks}
            addPrerequisite={addPrerequisite}
            updatePrerequisiteInputValue={updatePrerequisiteInputValue}
            removePrerequisite={removePrerequisite}
            updateWorkflowInstanceName={updateWorkflowInstanceName}
            actionDefinitions={actionDefinitions}
            workflows={workflows}
            existingPrerequisites={existingPrerequisites}
            prompts={prompts}
            contexts={contexts}
            embeddingTopics={embeddingTopics}
            allowEditing={allowEditing}
          />
        </div>
      </div>
      <ConfirmDialog
        open={showingDeleteDialog}
        onSuccess={onDelete}
        onClose={() => showDeleteDialog(false)}
        title="Delete workflow"
        description="Are you sure you want to delete this workflow?"
        okButtonCaption="Yes"
        cancelButtonCaption="No"
      />
      {workflow.is_data_asset ? (
        <DataAssetDuplicateConfirmDialog
          dataAsset={workflow}
          open={showingDuplicateDialog}
          onSuccess={duplicateWorkflow}
          onClose={() => showDuplicateDialog(false)}
          workflows={workflows}
          organisationId={organisationId}
        />
      ) : (
        <ConfirmDialog
          open={showingDuplicateDialog}
          onSuccess={duplicateWorkflow}
          onClose={() => showDuplicateDialog(false)}
          title="Duplicate workflow"
          description="Are you sure you want to duplicate this workflow?"
          okButtonCaption="Yes"
          cancelButtonCaption="No"
        />
      )}

      {workflow.is_data_asset ? (
        <DataAssetLockConfirmDialog
          dataAsset={workflow}
          open={showingToggleLockDialog}
          onSuccess={() => onToggleUpdate("is_locked")}
          onClose={() => showLockWorkflowDialog(false)}
        />
      ) : (
        <WorkflowLockConfirmDialog
          workflow={workflow}
          open={showingToggleLockDialog}
          onSuccess={() => onToggleUpdate("is_locked")}
          onClose={() => showLockWorkflowDialog(false)}
        />
      )}
    </div>
  );
};

function TopMenu({
  organisationId,
  showAllowEditingCheckbox,
  workflow,
  showDuplicateDialog,
  allowEditing,
  setAllowEditing,
}) {
  const disableDuplicate = workflow.is_data_asset && !workflow.is_locked;
  const showDataAssetLink = workflow.is_data_asset && workflow.is_locked;

  const duplicateButton = (
    <Button
      style={{marginRight: "2em"}}
      variant="contained"
      disabled={disableDuplicate}
      onClick={() => showDuplicateDialog(true)}
    >
      Duplicate
    </Button>
  );

  const handleAllowEditingChange = event => {
    setAllowEditing(event.target.checked);
  };

  return (
    <div style={{display: "flex", alignItems: "center"}}>
      {showAllowEditingCheckbox && (
        <FormControlLabel
          label={"Allow Editing"}
          control={
            <Checkbox
              checked={allowEditing}
              onChange={event => handleAllowEditingChange(event)}
              color="primary"
            />
          }
        />
      )}
      {disableDuplicate ? (
        <Tooltip title="You can only duplicate a locked data asset">
          <span>{duplicateButton}</span>
        </Tooltip>
      ) : (
        duplicateButton
      )}
      {showDataAssetLink && (
        <Link
          style={{
            fontWeight: "bold",
            width: "7em",
            flexShrink: 0,
            color: "inherit",
            textDecoration: "none",
            marginRight: "1em",
          }}
          to={`/organisation/${organisationId}/data-assets/definition/${workflow.id}`}
        >
          Data Asset
        </Link>
      )}
      <Link
        style={{
          fontWeight: "bold",
          width: "5em",
          flexShrink: 0,
          color: "inherit",
          textDecoration: "none",
          marginRight: "1em",
        }}
        to={`/organisation/${organisationId}/llm_run/list?workflow=${workflow.id}`}
      >
        Runs
      </Link>
      <Link
        style={{
          fontWeight: "bold",
          width: "5em",
          flexShrink: 0,
          color: "inherit",
          textDecoration: "none",
        }}
        to={`/organisation/${organisationId}/workflow/list${
          workflow.is_data_asset ? "?showDataAssets=true" : ""
        }`}
      >
        Back
      </Link>
    </div>
  );
}

export default WorkflowDetail;
